import { FIX_KIND, FIX_SECTION } from '../services/holdReasonFix';
import { HOLD_SUBJECT, HOLD_TASK_TYPES } from './holdTaskTypes';

/** What kind of facts a task's card lists, each read and worded by its own reader. */
export const HOLD_TASK_FACTS = {
  /** Each payer and code no authorization covers, with the service dates. */
  UNCOVERED_SERVICES: 'uncoveredServices',
  /** Each field a claim needs that the held items say is missing. */
  MISSING_FIELDS: 'missingFields',
  /** Which of several causes sharing one fix the held items have. */
  CAUSES: 'causes',
};

const fixedOnClient = (section) => ({ kind: FIX_KIND.CLIENT, section });
const PAYER_FIX = { kind: FIX_KIND.FUNDING_SOURCE };
const TREATMENT_SERVICE_FIX = { kind: FIX_KIND.TREATMENT_SERVICE };
const SESSION_FIX = { kind: FIX_KIND.SESSION_ACTIVITY };
/** The section is the half of the delivery the session, or the plan's sessions, are missing. */
const fixedOnSessionDelivery = (section) => ({ kind: FIX_KIND.SESSION_DELIVERY, section });
const fixedOnSessionPlan = (section) => ({ kind: FIX_KIND.SESSION_PLAN, section });
/** A performer is recorded by login, which is what the staff editor is opened on. */
const fixedOnStaff = (section) => ({ kind: FIX_KIND.STAFF, section });
/** One charge master per clinic, so a missing charge, though about one code, is fixed on the clinic's. */
const CHARGE_MASTER_FIX = { kind: FIX_KIND.CHARGE_MASTER, on: 'clinic' };

/** Items held for one of `reasons`, and matching `also` field by field, as the projector counts them. */
const clause = (reasons, also = {}) => ({ reasons, also });

/**
 * Everything this context's screens know about each task type it raises, in one place: what it is
 * about, which items it counts, where it is fixed, and which facts its card lists. A screen reads
 * the entry rather than asking which type it has, so a new type is a new entry, and a new kind of
 * fact is a new reader, not a new branch.
 *
 * A fix is made on the task's subject, unless `on` says the clinic. The projector's `HOLD_TASKS` is
 * the other half, which `backendContract` pins: the same types, subjects and clauses, and facts
 * read from a reason the type counts.
 *
 * Other contexts will raise tasks too. Each describes its own in its own catalog; nothing here is
 * shared with them, and the worklist page composes them.
 *
 * @type {Object<string, {subject: string,
 *   clauses: {reasons: string[], also: Object<string, *>}[],
 *   fix: {kind: string, section?: string, on?: 'clinic'},
 *   facts?: {kind: string, holdReason?: string, field?: string, holdReasons?: string[]}}>}
 */
export const HOLD_TASK_CATALOG = {
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_MISSING]: {
    subject: HOLD_SUBJECT.CLIENT, clauses: [clause(['no_coverage'])], fix: fixedOnClient(FIX_SECTION.COVERAGE),
  },
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_UNVERIFIED]: {
    subject: HOLD_SUBJECT.CLIENT, clauses: [clause(['coverage_unverified'])], fix: fixedOnClient(FIX_SECTION.COVERAGE),
  },
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_ON_HOLD]: {
    subject: HOLD_SUBJECT.CLIENT, clauses: [clause(['coverage_on_hold'])], fix: fixedOnClient(FIX_SECTION.COVERAGE),
  },
  [HOLD_TASK_TYPES.CLIENT_POLICYHOLDER]: {
    subject: HOLD_SUBJECT.CLIENT,
    clauses: [clause(['coverage_policyholder_incomplete', 'coverage_policyholder_unresolved'])],
    fix: fixedOnClient(FIX_SECTION.COVERAGE),
    facts: { kind: HOLD_TASK_FACTS.MISSING_FIELDS, holdReason: 'coverage_policyholder_incomplete' },
  },
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_PAYER]: {
    subject: HOLD_SUBJECT.CLIENT,
    clauses: [clause(['no_funder_as_of'], { fundingSourceId: null })],
    fix: fixedOnClient(FIX_SECTION.COVERAGE),
  },
  [HOLD_TASK_TYPES.CLIENT_AUTHORIZATIONS]: {
    subject: HOLD_SUBJECT.CLIENT,
    clauses: [clause(['held_for_auth'])],
    fix: fixedOnClient(FIX_SECTION.AUTHORIZATIONS),
    facts: { kind: HOLD_TASK_FACTS.UNCOVERED_SERVICES, holdReason: 'held_for_auth' },
  },
  [HOLD_TASK_TYPES.CLIENT_DIAGNOSIS]: {
    subject: HOLD_SUBJECT.CLIENT, clauses: [clause(['no_diagnosis'])], fix: fixedOnClient(FIX_SECTION.DIAGNOSES),
  },
  [HOLD_TASK_TYPES.CLIENT_DEMOGRAPHICS]: {
    subject: HOLD_SUBJECT.CLIENT,
    clauses: [clause(['patient_demographics_incomplete'])],
    fix: fixedOnClient(FIX_SECTION.DEMOGRAPHICS),
    facts: { kind: HOLD_TASK_FACTS.MISSING_FIELDS, holdReason: 'patient_demographics_incomplete' },
  },
  [HOLD_TASK_TYPES.PAYER_NOT_IN_EFFECT]: {
    subject: HOLD_SUBJECT.PAYER, clauses: [clause(['no_funder_as_of'])], fix: PAYER_FIX,
  },
  [HOLD_TASK_TYPES.PAYER_INCOMPLETE_SETUP]: {
    subject: HOLD_SUBJECT.PAYER, clauses: [clause(['incomplete_funder_config'])], fix: PAYER_FIX,
  },
  [HOLD_TASK_TYPES.PAYER_UNSUPPORTED_BILLING]: {
    subject: HOLD_SUBJECT.PAYER, clauses: [clause(['unsupported_bills_under'])], fix: PAYER_FIX,
  },
  [HOLD_TASK_TYPES.PAYER_NO_MODIFIER_RULE]: {
    subject: HOLD_SUBJECT.PAYER, clauses: [clause(['no_modifier_tier'])], fix: PAYER_FIX,
  },
  [HOLD_TASK_TYPES.PAYER_TOO_MANY_MODIFIERS]: {
    subject: HOLD_SUBJECT.PAYER, clauses: [clause(['too_many_modifiers'])], fix: PAYER_FIX,
  },
  [HOLD_TASK_TYPES.CHARGE_MASTER]: {
    subject: HOLD_SUBJECT.CLINIC, clauses: [clause(['no_charge_master'])], fix: CHARGE_MASTER_FIX,
  },
  [HOLD_TASK_TYPES.CHARGE_FOR_CODE]: {
    subject: HOLD_SUBJECT.CODE, clauses: [clause(['no_charge'])], fix: CHARGE_MASTER_FIX,
  },
  [HOLD_TASK_TYPES.SERVICE_NOT_BILLABLE]: {
    subject: HOLD_SUBJECT.TREATMENT_SERVICE, clauses: [clause(['not_billable'])], fix: TREATMENT_SERVICE_FIX,
  },
  [HOLD_TASK_TYPES.SERVICE_CODE_INVALID]: {
    subject: HOLD_SUBJECT.TREATMENT_SERVICE,
    clauses: [clause(['no_code_source', 'code_not_valid_on_date', 'manually_entered_code'])],
    fix: TREATMENT_SERVICE_FIX,
    facts: {
      kind: HOLD_TASK_FACTS.CAUSES,
      field: 'treatmentServiceId',
      holdReasons: ['no_code_source', 'code_not_valid_on_date', 'manually_entered_code'],
    },
  },
  [HOLD_TASK_TYPES.SESSION_SERVICE_MISSING]: {
    subject: HOLD_SUBJECT.SESSION,
    clauses: [clause(['no_treatment_service'], { sessionPlanDeclaration: null })],
    fix: fixedOnSessionDelivery(FIX_SECTION.TREATMENT_SERVICES),
  },
  [HOLD_TASK_TYPES.SESSION_SERVICE_DUPLICATE]: {
    subject: HOLD_SUBJECT.SESSION, clauses: [clause(['duplicate_treatment_service'])], fix: SESSION_FIX,
  },
  [HOLD_TASK_TYPES.SESSION_PLACE_OF_SERVICE]: {
    subject: HOLD_SUBJECT.SESSION,
    clauses: [clause(['no_place_of_service'], { sessionPlanDeclaration: null })],
    fix: fixedOnSessionDelivery(FIX_SECTION.PLACE_OF_SERVICE),
  },
  [HOLD_TASK_TYPES.SESSION_PLAN_SERVICE_MISSING]: {
    subject: HOLD_SUBJECT.SESSION_PLAN,
    clauses: [clause(['no_treatment_service'])],
    fix: fixedOnSessionPlan(FIX_SECTION.TREATMENT_SERVICES),
  },
  [HOLD_TASK_TYPES.SESSION_PLAN_PLACE_OF_SERVICE]: {
    subject: HOLD_SUBJECT.SESSION_PLAN,
    clauses: [clause(['no_place_of_service'])],
    fix: fixedOnSessionPlan(FIX_SECTION.PLACE_OF_SERVICE),
  },
  [HOLD_TASK_TYPES.SESSION_PERFORMER]: {
    subject: HOLD_SUBJECT.SESSION, clauses: [clause(['no_performer'])], fix: SESSION_FIX,
  },
  [HOLD_TASK_TYPES.SESSION_NOT_PAYABLE]: {
    subject: HOLD_SUBJECT.SESSION, clauses: [clause(['tier_not_payable'])], fix: SESSION_FIX,
  },
  [HOLD_TASK_TYPES.STAFF_NPI]: {
    subject: HOLD_SUBJECT.STAFF, clauses: [clause(['no_rendering_provider'])], fix: fixedOnStaff(FIX_SECTION.PROVIDER),
  },
  [HOLD_TASK_TYPES.STAFF_QUALIFICATION]: {
    subject: HOLD_SUBJECT.STAFF,
    clauses: [clause(['performer_no_qualification'])],
    fix: fixedOnStaff(FIX_SECTION.QUALIFICATIONS),
  },
};

/** The catalog's entry for a task type, or null for one this context does not raise. */
export const holdTaskEntryOf = (type) => (Object.prototype.hasOwnProperty.call(HOLD_TASK_CATALOG, type)
  ? HOLD_TASK_CATALOG[type]
  : null);

/** Whether this context raises tasks of this type. */
export const isHoldTaskType = (type) => holdTaskEntryOf(type) !== null;

/** What a task of this type is about. */
export const holdSubjectOf = (type) => holdTaskEntryOf(type)?.subject ?? null;
/**
 * Whether a billing item is one of those a task of this type blocks, as the projector counts them
 * for the task's total.
 *
 * @param {{holdReasons: string[]}} item
 * @param {string} type
 */
export const isBlockedBy = (item, type) => (holdTaskEntryOf(type)?.clauses ?? [])
  .some(({ reasons, also }) => reasons.some((reason) => item.holdReasons.includes(reason))
    && Object.entries(also).every(([field, value]) => (item[field] ?? null) === value));
